// Ponto de entrada do backend falso (M1 §8 "Before the contract exists"; Identity desde o GIY-037; Tenancy desde o GIY-046; Pessoas desde o GIY-067; delegation log desde o GIY-075):
// `createFakeApiRouter(me, auth)` cria um estado isolado por instância de `FakeAuthBackend` e devolve um `handle`
// chamado pelo `fetch` de substituição para todo caminho que `createFakeFetch` (M0) ainda não trata.
import type { MeResponse } from '@giyus/api-client';

import type { FakeSessionAuth } from '../auth/FakeAuthProvider';
import { isGreenfieldAdmin } from '../auth/greenfield';
import { handleAuthzRoute } from './authzRoutes';
import { seedAuthzState } from './authzStore';
import { handleDelegationRoute } from './delegationRoutes';
import { seedDelegationState } from './delegationStore';
import { handleHomeRoute } from './homeRoutes';
import { handleIdentityRoute } from './identityRoutes';
import { seedIdentityState } from './identityStore';
import { seedNotificationsState } from './notificationsStore';
import { handleNotificationsRoute } from './notificationsRoutes';
import { handlePeopleRoute, withPeopleApprovals } from './peopleRoutes';
import { seedPeopleState } from './peopleStore';
import { handleTenancyRoute } from './tenancyRoutes';
import { seedTenancyState } from './tenancyStore';
import { seedWorkflowState } from './workflowStore';
import { handleWorkflowRoute } from './workflowRoutes';
import {
  handleSuppliersAndStudiesRoute,
  seedSuppliersAndStudiesState,
} from './suppliersAndStudiesRoutes';

export interface FakeApiRouter {
  readonly handle: (request: Request, correlationId: string | null) => Promise<Response | null>;
}

const alwaysRecent: FakeSessionAuth = { isRecentlyAuthenticated: () => true };

/** Cria um roteador com estado próprio (mutável entre chamadas, descartado ao recarregar a página). */
export function createFakeApiRouter(
  me: MeResponse,
  auth: FakeSessionAuth = alwaysRecent,
): FakeApiRouter {
  const greenfield = isGreenfieldAdmin(me);
  const identity = seedIdentityState(me);
  const tenancy = seedTenancyState();
  const authz = seedAuthzState(me);
  const people = seedPeopleState(me);
  const delegation = seedDelegationState(me, people);
  const wf = seedWorkflowState();
  const ntf = seedNotificationsState();
  const supAndStu = seedSuppliersAndStudiesState(greenfield);

  if (greenfield) {
    tenancy.tenants.splice(0);
    tenancy.ownership.splice(0);
    delegation.studies.splice(0);
    delegation.logs.splice(0);
    delegation.delegations.splice(0);
    delegation.attempts.splice(0);
    wf.tasks.splice(0);
    wf.exceptions.splice(0);
    wf.approvals.splice(0);
    ntf.templates.splice(0);
    ntf.testSends.splice(0);
  } else {
    linkSeededTemplateApproval(wf, ntf);
  }

  return {
    async handle(request, correlationId) {
      const url = new URL(request.url);
      const segments = url.pathname
        .replace(/^\/api\/v1\//, '')
        .split('/')
        .filter(Boolean);
      if (!url.pathname.startsWith('/api/v1/')) {
        return null;
      }
      const identityResponse = await handleIdentityRoute(request, segments, {
        state: identity,
        me,
        correlationId,
        auth,
      });
      if (identityResponse) {
        return identityResponse;
      }
      const tenancyResponse = await handleTenancyRoute(request, segments, {
        state: tenancy,
        me,
        correlationId,
      });
      if (tenancyResponse) {
        return tenancyResponse;
      }
      // Pessoas (GIY-067) antes da Autorização: intercepta só a segunda chave das aprovações de pessoas (Matriz e exceção de
      // transição) na rota genérica `/authz/two-key/{id}/second-key`; as demais seguem adiante.
      const peopleContext = { state: people, me, correlationId, auth, authz };
      const peopleResponse = await handlePeopleRoute(request, segments, peopleContext);
      if (peopleResponse) {
        return peopleResponse;
      }
      // Delegation log (GIY-075): `tenants/{t}/studies/{s}/delegation-log/*` e `me/delegations*`, julgados contra o estado de Pessoas.
      const delegationResponse = await handleDelegationRoute(request, segments, {
        state: delegation,
        people,
        me,
        correlationId,
        auth,
        authz,
      });
      if (delegationResponse) {
        return delegationResponse;
      }
      const authzResponse = await handleAuthzRoute(request, segments, {
        state: authz,
        me,
        correlationId,
        auth,
      });
      if (authzResponse) {
        // As pendências de duas chaves de Pessoas somam-se às da Autorização (`GET /authz/two-key`).
        return withPeopleApprovals(request, segments, authzResponse, peopleContext);
      }
      const workflowResponse = await handleWorkflowRoute(request, segments, {
        state: wf,
        me,
        correlationId,
      });
      if (workflowResponse) {
        return workflowResponse;
      }
      const homeResponse = handleHomeRoute(request, segments, supAndStu);
      if (homeResponse) {
        return homeResponse;
      }
      const supStuResponse = await handleSuppliersAndStudiesRoute(request, segments, supAndStu);
      if (supStuResponse) {
        return supStuResponse;
      }
      return handleNotificationsRoute(request, segments, { wf, ntf, me, correlationId });
    },
  };
}

/** Liga a aprovação semeada em `workflowStore` à versão de template semeada em `notificationsStore`
 * (os dois seeds nascem independentes; a ligação evita duplicar dados de aprovação nos dois lugares). */
function linkSeededTemplateApproval(
  wf: ReturnType<typeof seedWorkflowState>,
  ntf: ReturnType<typeof seedNotificationsState>,
): void {
  const approval = wf.approvals.find(
    (a) => a.objectType === 'MessageTemplate' && a.objectId === 'task.assigned',
  );
  const template = ntf.templates.find((t) => t.key === 'task.assigned');
  const version = template?.versions.find((v) => v.status === 'InReview');
  if (approval && template && version) {
    version.approvalRequestId = approval.id;
    template.pendingApprovalId = approval.id;
  }
}
